import { compact } from 'lodash'
import { useLingui } from '@lingui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ArticleIcon from '@mui/icons-material/Article'
import Biotech from '@mui/icons-material/Biotech'
import ContactPageIcon from '@mui/icons-material/ContactPage'
import EmailIcon from '@mui/icons-material/Email'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'

import ConsultationDocumentsIcon from '@/components/ui/icons/ConsultationDocuments'
import MedicalVideoIcon from '@/components/ui/icons/MedicalVideo'
import PatientIdentityIcon from '@/components/ui/icons/PatientIdentity'
import StethoscopeIcon from '@/components/ui/icons/Stethoscope'
import VitalParametersIcon from '@/components/ui/icons/VitalParameters'
import { useMedicalActsLicenseEnabled } from '@/features/medical-acts/model/useMedicalActsLicenseEnabled'
import { useReportLicenseEnabled } from '@/features/report'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useShowDMP } from '@/features/dmp-connect'
import { skipToken } from '@reduxjs/toolkit/query'

export enum SidebarMenuItem {
  PATIENT_INFO = 'patientInfo',
  DOCUMENTS = 'documents',
  CONSULTATIONS = 'consultations',
  TRANSCRIPTION = 'transcription',
  FORMS = 'forms',
  MEDICAL_ACTS = 'medicalActs',
  DMP = 'dmp',
  MSS = 'mss',
}

export enum EquipmentMenuItem {
  MESURES = 'mesures',
  MEDICAL_VIDEO = 'medicalVideo',
  BIOLOGICALS = 'biologicals',
  STETHOSCOPE = 'stethoscope',
}

export function useSidebarMenu(consultationId?: string) {
  const lingui = useLingui()
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const hasMedicalActsLicense = useMedicalActsLicenseEnabled()
  // The AI scribe's on/off switch: the `reports` service on the user's licenses (also enforced server-side).
  const hasReportsLicense = useReportLicenseEnabled()

  const { data: consultation } = useGetConsultationQuery(consultationId ?? skipToken)
  const showDMP = useShowDMP(consultation?.patient._id)

  const patientMenuItems = useMemo(() => {
    return compact([
      {
        id: SidebarMenuItem.PATIENT_INFO,
        icon: PatientIdentityIcon,
        label: t('page.localConsultation.sidebar.patientInfo.label'),
        isNested: false,
      },
      {
        id: SidebarMenuItem.DOCUMENTS,
        icon: ConsultationDocumentsIcon,
        label: t('page.localConsultation.sidebar.documents.label'),
        isNested: false,
      },
      {
        id: SidebarMenuItem.CONSULTATIONS,
        icon: ContactPageIcon,
        label: t('page.localConsultation.sidebar.historicalConsultations.label'),
        isNested: false,
      },
      hasReportsLicense && {
        id: SidebarMenuItem.TRANSCRIPTION,
        icon: RecordVoiceOverIcon,
        label: t('page.liveConsultation.sidebar.transcription.label'),
        isNested: false,
      },
      {
        id: SidebarMenuItem.FORMS,
        icon: ArticleIcon,
        label: t('page.localConsultation.sidebar.forms.label'),
        isNested: false,
      },
      hasMedicalActsLicense && {
        id: SidebarMenuItem.MEDICAL_ACTS,
        icon: LocalHospitalIcon,
        label: t('page.localConsultation.sidebar.medicalActs.label', { defaultValue: 'Medical Acts' }),
        isNested: false,
      },
      showDMP && {
        id: SidebarMenuItem.DMP,
        icon: FolderSharedIcon,
        label: lingui._(t`DMP`),
      },
      showDMP && {
        id: SidebarMenuItem.MSS,
        icon: EmailIcon,
        label: lingui._(t`MS Santé`),
      },
    ])
  }, [t, showDMP, hasMedicalActsLicense, hasReportsLicense, lingui])

  const equipmentMenuItems = useMemo(
    () => [
      {
        id: EquipmentMenuItem.MESURES,
        icon: VitalParametersIcon,
        label: t('page.localConsultation.sidebar.mesures.label'),
      },
      {
        id: EquipmentMenuItem.MEDICAL_VIDEO,
        icon: MedicalVideoIcon,
        label: t('page.liveConsultation.sidebar.medicalVideo.label'),
      },
      {
        id: EquipmentMenuItem.BIOLOGICALS,
        icon: Biotech,
        label: t('page.localConsultation.sidebar.biologicals.label'),
      },
      {
        id: EquipmentMenuItem.STETHOSCOPE,
        icon: StethoscopeIcon,
        label: t('page.localConsultation.sidebar.stethoscope.label'),
        isNested: isMobileScreen,
      },
    ],
    [isMobileScreen, t]
  )

  const [selectedPatientMenuItem, setSelectedPatientMenuItem] = useState<string>(patientMenuItems[0].id)
  const [selectedEquipmentMenuItem, setSelectedEquipmentMenuItem] = useState<string>(equipmentMenuItems[0].id)

  return {
    patientMenuItems,
    equipmentMenuItems,
    selectedPatientMenuItem,
    selectedEquipmentMenuItem,
    setSelectedPatientMenuItem,
    setSelectedEquipmentMenuItem,
    isMobileScreen,
  }
}
